// Platform console → 💲 Plans & pricing (docs/CLINTOBYTES_WEBSITE_DESIGN.md §B8.1).
// What clintobytes.health shows: each plan's text, features and prices, and the
// add-ons. A plan or add-on is a draft until it is published. Editing a plan's
// features changes no existing hospital — only a later "apply plan" picks it up.
import { useEffect, useState } from 'react';
import { platformApi } from '../lib/platformApi/client';
import { ConfirmDialog } from './ConfirmDialog';
import { formatPlanPrice } from './format';

interface Plan {
  key: string; name: string; tagline: string | null; audience: string | null; description: string | null;
  highlights: string[]; features: string[]; price_monthly_inr: string | null; price_yearly_inr: string | null;
  price_label: string | null; badge: string | null; limits: Record<string, number>; published: boolean; published_at: string | null;
  self_serve: boolean; display_order: number; updated_at: string; publish_problems: string[]; editable: boolean;
}
interface Addon {
  key: string; name: string; description: string | null; features: string[]; available_on: string[];
  price_monthly_inr: string | null; price_unit: 'hospital' | 'doctor'; published: boolean; display_order: number;
  updated_at: string; publish_problems: string[];
}
interface Feature { key: string; label: string; depends_on: string[] }
interface Data { plans: Plan[]; addons: Addon[]; features: Feature[]; limit_keys: Record<string, string> }

type Editing = { kind: 'plans'; row: Plan } | { kind: 'addons'; row: Addon };
type Confirming = { kind: 'plans' | 'addons'; row: Plan | Addon; publish: boolean };

const btn = { background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' } as const;
const primary = { ...btn, background: '#0B5FD6', color: '#fff', border: '1px solid #0B5FD6' } as const;
const cell = { padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: 13, verticalAlign: 'top' } as const;
const head = { ...cell, fontWeight: 600, color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E5E7EB' } as const;
const input = { width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' } as const;

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const inr = (v: string | null) => (v == null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);

function Badge({ published }: { published: boolean }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '2px 8px',
      background: published ? '#DCFCE7' : '#F3F4F6', color: published ? '#166534' : '#4B5563' }}>
      {published ? 'Published' : 'Draft'}
    </span>
  );
}

export default function PlansPricingScreen({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => platformApi.get<Data>('/plans').then(setData).catch((e) => setError(errText(e)));
  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!confirming) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await platformApi.post(`/${confirming.kind}/${confirming.row.key}/${confirming.publish ? 'publish' : 'unpublish'}`,
        { expected_updated_at: confirming.row.updated_at });
      setNotice(`${confirming.row.name} ${confirming.publish ? 'published' : 'taken off the website'}.`);
      setConfirming(null);
      await load();
    } catch (e) { setError(errText(e)); setConfirming(null); } finally { setBusy(false); }
  };

  const featureLabel = (k: string) => data?.features.find((f) => f.key === k)?.label || k;
  const planName = (k: string) => data?.plans.find((p) => p.key === k)?.name || k;

  if (editing && data) {
    return (
      <EditPanel editing={editing} data={data} onCancel={() => setEditing(null)}
        onSaved={async (name) => { setEditing(null); setNotice(`${name} saved.`); await load(); }} />
    );
  }

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 16px' }}>
      <button onClick={onBack} style={btn}>← Back</button>
      <h1 style={{ fontSize: 20, margin: '14px 0 4px' }}>💲 Plans &amp; pricing</h1>
      <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px', maxWidth: 820 }}>
        What clintobytes.health shows. Everything is a <strong>draft</strong> until you publish it. Visitors see a published
        change within five minutes; search engines after the next site rebuild. Changing a plan&apos;s features doesn&apos;t
        change any existing hospital. Prices are billed yearly; the monthly-billing price stays empty until monthly
        billing is offered.
      </p>
      {error && <div role="alert" style={{ background: '#FEF2F2', color: '#991B1B', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {notice && <div role="status" style={{ background: '#ECFDF5', color: '#065F46', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{notice}</div>}
      {!data ? <div style={{ fontSize: 13, color: '#6B7280' }}>Loading…</div> : (
        <>
          <h2 style={{ fontSize: 16, margin: '8px 0' }}>Plans</h2>
          <div style={{ overflowX: 'auto', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={head}>Plan</th><th style={head}>Status</th><th style={head}>Price on the site</th>
                <th style={head}>Monthly billing</th><th style={head}>Features</th><th style={head}>Still needs</th><th style={head} />
              </tr></thead>
              <tbody>
                {data.plans.map((p) => (
                  <tr key={p.key}>
                    <td style={cell}><strong>{p.name}</strong><div style={{ color: '#6B7280' }}>{p.tagline || '—'}</div></td>
                    <td style={cell}><Badge published={p.published} />{p.price_label && <div style={{ color: '#6B7280', marginTop: 4 }}>{p.price_label}</div>}{p.badge && <div style={{ color: '#0057D9', marginTop: 4, fontWeight: 600 }}>★ {p.badge}</div>}</td>
                    <td style={cell}>{p.key === 'legacy_full' ? 'Never shown' : formatPlanPrice(p)}</td>
                    <td style={cell}>{inr(p.price_monthly_inr)}</td>
                    <td style={cell}>{p.features.map(featureLabel).join(', ')}</td>
                    <td style={{ ...cell, color: '#B45309' }}>{p.publish_problems.join('; ') || '—'}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {p.editable && <button style={btn} onClick={() => setEditing({ kind: 'plans', row: p })}>Edit</button>}{' '}
                      {p.editable && (p.published
                        ? <button style={btn} onClick={() => setConfirming({ kind: 'plans', row: p, publish: false })}>Unpublish</button>
                        : <button style={primary} disabled={p.publish_problems.length > 0} title={p.publish_problems.join('; ')}
                            onClick={() => setConfirming({ kind: 'plans', row: p, publish: true })}>Publish</button>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={{ fontSize: 16, margin: '8px 0' }}>Add-ons</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={head}>Add-on</th><th style={head}>Status</th><th style={head}>Price</th>
                <th style={head}>Available on</th><th style={head}>Switches on</th><th style={head} />
              </tr></thead>
              <tbody>
                {data.addons.map((a) => (
                  <tr key={a.key}>
                    <td style={cell}><strong>{a.name}</strong><div style={{ color: '#6B7280' }}>{a.description || '—'}</div></td>
                    <td style={cell}><Badge published={a.published} /></td>
                    <td style={cell}>{a.price_monthly_inr == null ? 'Talk to us' : `${inr(a.price_monthly_inr)}/month${a.price_unit === 'doctor' ? ' per doctor' : ''}`}</td>
                    <td style={cell}>{a.available_on.length ? a.available_on.map(planName).join(', ') : 'Every plan'}</td>
                    <td style={cell}>{a.features.map(featureLabel).join(', ')}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      <button style={btn} onClick={() => setEditing({ kind: 'addons', row: a })}>Edit</button>{' '}
                      {a.published
                        ? <button style={btn} onClick={() => setConfirming({ kind: 'addons', row: a, publish: false })}>Unpublish</button>
                        : <button style={primary} disabled={a.publish_problems.length > 0} title={a.publish_problems.join('; ')}
                            onClick={() => setConfirming({ kind: 'addons', row: a, publish: true })}>Publish</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <ConfirmDialog
        open={!!confirming}
        onOpenChange={(o) => { if (!o) setConfirming(null); }}
        title={confirming?.publish ? `Publish ${confirming?.row.name}?` : `Take ${confirming?.row.name} off the website?`}
        description={confirming?.publish
          ? 'It will appear on clintobytes.health, with its price, within five minutes. Make sure the price is one you will honour.'
          : 'It will disappear from clintobytes.health within five minutes. Hospitals already on it are not affected.'}
        confirmLabel={busy ? 'Working…' : confirming?.publish ? 'Publish' : 'Unpublish'}
        variant={confirming?.publish ? 'primary' : 'danger'}
        onConfirm={publish}
      />
    </div>
  );
}

function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
      <span style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', color: '#6B7280', fontSize: 12, marginTop: 3 }}>{hint}</span>}
      {error && <span style={{ display: 'block', color: '#B91C1C', fontSize: 12, marginTop: 3 }}>{error}</span>}
    </label>
  );
}

function EditPanel({ editing, data, onCancel, onSaved }: {
  editing: Editing; data: Data; onCancel: () => void; onSaved: (name: string) => void;
}) {
  const isPlan = editing.kind === 'plans';
  const r = editing.row as Plan & Addon;
  const [f, setF] = useState(() => ({
    name: r.name || '', tagline: r.tagline || '', audience: r.audience || '', description: r.description || '',
    highlights: (r.highlights || []).join('\n'), features: [...(r.features || [])],
    price_yearly_inr: r.price_yearly_inr ?? '', price_monthly_inr: r.price_monthly_inr ?? '', price_label: r.price_label || '', badge: r.badge || '',
    limits: Object.fromEntries(Object.keys(data.limit_keys).map((k) => [k, r.limits?.[k] != null ? String(r.limits[k]) : ''])),
    self_serve: !!r.self_serve, available_on: [...(r.available_on || [])], price_unit: r.price_unit || 'hospital',
    display_order: String(r.display_order ?? 0), reason: '',
  }));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (list: 'features' | 'available_on', k: string) =>
    setF((s) => ({ ...s, [list]: s[list].includes(k) ? s[list].filter((x) => x !== k) : [...s[list], k] }));

  const save = async () => {
    setSaving(true); setError('');
    const blank = (v: string) => (v.trim() === '' ? null : v.trim());
    const body: Record<string, unknown> = isPlan ? {
      name: f.name, tagline: blank(f.tagline), audience: blank(f.audience), description: blank(f.description),
      highlights: f.highlights.split('\n').map((x) => x.trim()).filter(Boolean),
      features: f.features, price_yearly_inr: blank(String(f.price_yearly_inr)), price_monthly_inr: blank(String(f.price_monthly_inr)),
      price_label: blank(f.price_label), badge: blank(f.badge), self_serve: f.self_serve, display_order: Number(f.display_order),
      limits: Object.fromEntries(Object.entries(f.limits).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, Number(v)])),
    } : {
      name: f.name, description: blank(f.description), features: f.features, available_on: f.available_on,
      price_monthly_inr: blank(String(f.price_monthly_inr)), price_unit: f.price_unit, display_order: Number(f.display_order),
    };
    try {
      const res = await platformApi.put<{ unchanged?: boolean }>(`/${editing.kind}/${r.key}`, { ...body, expected_updated_at: r.updated_at, reason: blank(f.reason) });
      if (res?.unchanged) { setError('Nothing has changed.'); return; }
      onSaved(f.name);
    } catch (e) { setError(errText(e)); } finally { setSaving(false); }
  };

  const sellable = data.plans.filter((p) => p.key !== 'legacy_full');
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
      <button onClick={onCancel} style={btn}>← Back to Plans &amp; pricing</button>
      <h1 style={{ fontSize: 20, margin: '14px 0 4px' }}>Edit {isPlan ? 'plan' : 'add-on'}: {r.name}</h1>
      {r.published && <p style={{ fontSize: 13, color: '#B45309' }}>This is published: a saved change appears on the website within five minutes.</p>}
      {error && <div role="alert" style={{ background: '#FEF2F2', color: '#991B1B', padding: 10, borderRadius: 8, fontSize: 13, margin: '10px 0' }}>{error}</div>}

      <Field label="Name"><input style={input} value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
      {isPlan && <>
        <Field label="Tagline" hint="One short line under the plan name, e.g. “For clinics getting started”."><input style={input} value={f.tagline} onChange={(e) => set('tagline', e.target.value)} /></Field>
        <Field label="Who it is for"><input style={input} value={f.audience} onChange={(e) => set('audience', e.target.value)} /></Field>
      </>}
      <Field label="Description"><textarea style={{ ...input, minHeight: 70 }} value={f.description} onChange={(e) => set('description', e.target.value)} /></Field>
      {isPlan && (
        <Field label="Highlights" hint="One per line, up to 8. Only what the product does today."><textarea style={{ ...input, minHeight: 90 }} value={f.highlights} onChange={(e) => set('highlights', e.target.value)} /></Field>
      )}

      <fieldset style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 12 }}>
        <legend style={{ fontSize: 13, fontWeight: 600 }}>{isPlan ? 'Features in this plan' : 'Features this add-on switches on'}</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>
          {data.features.filter((ft) => isPlan || ft.key !== 'core').map((ft) => (
            <label key={ft.key} style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={f.features.includes(ft.key)} onChange={() => toggle('features', ft.key)} />
              {ft.label}
            </label>
          ))}
        </div>
      </fieldset>

      {isPlan ? <>
        <Field label="Price for a year (₹)" hint="Shown as a per-month price, billed yearly. Leave empty for “Talk to us”.">
          <input style={input} inputMode="decimal" value={String(f.price_yearly_inr)} onChange={(e) => set('price_yearly_inr', e.target.value)} />
        </Field>
        <Field label="Price per month if billed monthly (₹)" hint="Leave empty until monthly billing is offered (Phase 2B).">
          <input style={input} inputMode="decimal" value={String(f.price_monthly_inr)} onChange={(e) => set('price_monthly_inr', e.target.value)} />
        </Field>
        <Field label="Price label" hint="Shown next to the price, e.g. “Introductory”."><input style={input} value={f.price_label} onChange={(e) => set('price_label', e.target.value)} /></Field>
        <Field label="Highlight label (optional)" hint="Makes this the highlighted card on the Pricing page, with this text on its ribbon, e.g. “Clinics with a pharmacy”. Say who it suits — never “most popular” unless that is measured."><input style={input} value={f.badge} onChange={(e) => set('badge', e.target.value)} /></Field>
        <fieldset style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <legend style={{ fontSize: 13, fontWeight: 600 }}>Limits and extra-unit prices (optional)</legend>
          {Object.entries(data.limit_keys).map(([k, label]) => (
            <Field key={k} label={label}><input style={input} inputMode="decimal" value={f.limits[k]} onChange={(e) => setF((s) => ({ ...s, limits: { ...s.limits, [k]: e.target.value } }))} /></Field>
          ))}
        </fieldset>
        <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
          <input type="checkbox" checked={f.self_serve} onChange={(e) => set('self_serve', e.target.checked)} />
          Can be bought online (once checkout exists, Phase 2B). A plan bought online needs a price.
        </label>
      </> : <>
        <Field label="Price per month (₹)" hint="Leave empty for “Talk to us”.">
          <input style={input} inputMode="decimal" value={String(f.price_monthly_inr)} onChange={(e) => set('price_monthly_inr', e.target.value)} />
        </Field>
        <Field label="Charged per">
          <select style={input} value={f.price_unit} onChange={(e) => set('price_unit', e.target.value)}>
            <option value="hospital">Hospital</option><option value="doctor">Doctor</option>
          </select>
        </Field>
        <fieldset style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <legend style={{ fontSize: 13, fontWeight: 600 }}>Available on (none ticked = every plan)</legend>
          {sellable.map((p) => (
            <label key={p.key} style={{ fontSize: 13, display: 'inline-flex', gap: 6, alignItems: 'center', marginRight: 16 }}>
              <input type="checkbox" checked={f.available_on.includes(p.key)} onChange={() => toggle('available_on', p.key)} />{p.name}
            </label>
          ))}
        </fieldset>
      </>}
      <Field label="Display order" hint="Lower numbers come first."><input style={input} inputMode="numeric" value={f.display_order} onChange={(e) => set('display_order', e.target.value)} /></Field>
      <Field label="Reason for the change (optional, kept in the audit trail)"><input style={input} value={f.reason} onChange={(e) => set('reason', e.target.value)} /></Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button style={btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
